<template>
	<div>
		<main class="container-xl py-3">

			<div v-if="loadError" class="rounded mb-3 px-3 py-2"
				style="background:#fff1f2;border:1px solid #fecdd3;font-size:12px;color:#9f1239;">{{ loadError }}</div>

			<div v-if="loading" class="text-center py-5">
				<div class="spinner-border" role="status"></div>
			</div>

			<template v-else>

				<!-- Actions row + auto-save indicator -->
				<div class="d-flex align-items-baseline justify-content-end gap-2 flex-wrap mb-3">
					<div class="me-auto" style="font-size:11px;"
						:style="{ color: saving ? '#64748b' : (savedAt ? '#047857' : '#94a3b8'), fontStyle: !savedAt && !saving ? 'italic' : 'normal' }">
						<template v-if="saving">
							<span class="d-inline-block rounded-circle me-1 align-middle"
								style="width:8px;height:8px;background:#f59e0b;animation:cwpePulse 1.2s ease-in-out infinite;"></span>
							Saving…
						</template>
						<template v-else-if="savedAt">
							✓ Auto-saved at {{ savedAt.toLocaleTimeString() }} — safe to navigate away and back
						</template>
						<template v-else-if="!form.municipality_name_raw || !form.state">
							Pick a municipality to start — changes will auto-save from there
						</template>
						<template v-else>
							Will auto-save on next change
						</template>
					</div>
					<router-link to="/cwpe/settings" class="btn btn-sm" style="border:1px solid #cbd5e1;background:white;color:#334155;font-size:12px;padding:6px 10px;">
						Edit line-item pricing
					</router-link>
					<button v-if="!isNew" type="button" @click="onDelete"
						class="btn btn-sm" style="border:1px solid #fecdd3;background:white;color:#9f1239;font-size:12px;padding:6px 10px;">
						Delete quote
					</button>
				</div>

				<!-- Muni search -->
				<div class="rounded bg-white p-3 mb-3" style="border:1px solid #e2e8f0;">
					<label class="form-label mb-1" style="font-size:12px;font-weight:500;color:#475569;">Municipality</label>
					<div class="position-relative">
						<input ref="muniInput" type="text"
							:value="muniDisplayValue"
							@input="onMuniInput"
							@focus="onMuniFocus"
							@blur="onMuniBlur"
							placeholder="Type a city, town, or village name…"
							class="form-control" />
						<div v-if="muniShowDropdown && muniSuggestions.length"
							class="position-absolute w-100 rounded shadow"
							style="top:100%;z-index:1000;background:white;border:1px solid #e2e8f0;max-height:280px;overflow-y:auto;margin-top:4px;">
							<button v-for="s in muniSuggestions" :key="s.id" type="button"
								@mousedown.prevent="selectMuni(s)"
								class="btn w-100 text-start text-decoration-none rounded-0 px-3 py-2"
								style="border:none;border-bottom:1px solid #f1f5f9;font-size:14px;">
								<div class="fw-medium text-dark">
									{{ s.name }}<span class="fw-normal" style="color:#94a3b8;">, {{ s.state }}</span>
								</div>
								<div style="font-size:11px;color:#64748b;">{{ (s.county || '—') }} <span v-if="s.level">· {{ s.level }}</span></div>
							</button>
						</div>
					</div>
					<div v-if="muniLoading" class="mt-2" style="font-size:12px;color:#64748b;">Loading muni context…</div>
					<div v-if="muniError" class="mt-2" style="font-size:12px;color:#9f1239;">{{ muniError }}</div>
					<div v-if="form.municipality_name_raw && form.population != null" class="mt-2" style="font-size:11px;color:#475569;">
						Pop {{ Number(form.population).toLocaleString() }}
						<template v-if="stateAdjustPctDisplay !== null">
							· state tier adjustment {{ stateAdjustPctDisplay >= 0 ? '+' : '' }}{{ stateAdjustPctDisplay.toFixed(1) }}%
						</template>
						<template v-if="snapPctDisplay !== null">
							· SNAP {{ snapPctDisplay.toFixed(1) }}%
						</template>
						<template v-if="popTrendDisplay !== null">
							· 5yr trend {{ popTrendDisplay >= 0 ? '+' : '' }}{{ popTrendDisplay.toFixed(1) }}%
						</template>
					</div>
				</div>

				<!-- Proposal preview -->
				<div v-if="breakdown" class="cwpe-quote-preview-sticky">
					<h2 class="h6 fw-semibold text-dark mb-2">Proposal preview</h2>
					<div class="rounded bg-white" style="border:1px solid #e2e8f0;">
						<header class="px-3 py-2 d-flex align-items-baseline justify-content-between gap-2"
							style="background:#f8fafc;border-bottom:1px solid #e2e8f0;">
							<div>
								<div class="fw-semibold text-dark" style="font-size:14px;">
									{{ form.municipality_name_raw }}, {{ form.state }}
									<span class="text-uppercase ms-2 rounded px-2 py-1 fw-bold" style="font-size:10px;background:#f1f5f9;color:#1e293b;">{{ form.bundle_id }}</span>
								</div>
								<div style="font-size:11px;color:#64748b;">
									{{ bundleName(form.bundle_id) }} · support: {{ supportTierName(form.support_tier_id) }}
									<span v-if="form.population"> · pop {{ Number(form.population).toLocaleString() }}</span>
								</div>
							</div>
							<div class="d-flex gap-1">
								<button v-for="yr in [1, 3, 5]" :key="yr" type="button"
									@click="form.horizon_years = yr"
									class="btn btn-sm rounded fw-medium"
									:style="form.horizon_years === yr
										? { background:'#1d4ed8', color:'white', fontSize:'12px', padding:'4px 10px', border:'1px solid #1d4ed8' }
										: { background:'white', color:'#475569', fontSize:'12px', padding:'4px 10px', border:'1px solid #cbd5e1' }">
									{{ yr }}-yr
								</button>
							</div>
						</header>
						<div class="row g-0">
							<div class="col-4 text-center p-3" style="border-right:1px solid #e2e8f0;">
								<div class="text-uppercase fw-semibold mb-1" style="font-size:10px;letter-spacing:0.05em;color:#64748b;">Setup</div>
								<div class="fw-bold font-monospace" style="font-size:22px;color:#0f172a;">{{ fmtCents(breakdown.totalSetupCents) }}</div>
								<div style="font-size:11px;color:#94a3b8;">one-time</div>
							</div>
							<div class="col-4 text-center p-3" style="border-right:1px solid #e2e8f0;">
								<div class="text-uppercase fw-semibold mb-1" style="font-size:10px;letter-spacing:0.05em;color:#64748b;">Annual</div>
								<div class="fw-bold font-monospace" style="font-size:22px;color:#0f172a;">{{ fmtCents(breakdown.totalAnnualCents) }}</div>
								<div style="font-size:11px;color:#94a3b8;">recurring</div>
							</div>
							<div class="col-4 text-center p-3" style="background:rgba(219,234,254,0.4);">
								<div class="text-uppercase fw-semibold mb-1" style="font-size:10px;letter-spacing:0.05em;color:#1d4ed8;">Year 1</div>
								<div class="fw-bold font-monospace" style="font-size:22px;color:#1e3a8a;">{{ fmtCents(breakdown.firstYearCents) }}</div>
								<div style="font-size:11px;color:#1d4ed8;">setup + annual</div>
							</div>
						</div>
						<div v-if="breakdown.horizons && breakdown.horizons.length" class="px-3 py-2"
							style="border-top:1px solid #e2e8f0;background:#fafbfc;font-size:12px;color:#475569;">
							<div class="d-flex flex-wrap gap-3">
								<div v-for="h in breakdown.horizons" :key="h.years"
									:style="{ fontWeight: h.years === form.horizon_years ? 600 : 400 }">
									<span style="color:#94a3b8;">{{ h.years }}-yr total:</span>
									<span class="font-monospace ms-1">{{ fmtCents(h.totalCents) }}</span>
									<span style="color:#94a3b8;font-size:11px;"> ({{ fmtCents(h.effectiveAnnualCents) }}/yr eff)</span>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Bundle picker -->
				<div class="rounded bg-white p-3 mb-3" style="border:1px solid #e2e8f0;">
					<h2 class="h6 fw-semibold text-dark mb-2">Bundle tier</h2>
					<div v-if="!bundles.length" class="text-center py-2" style="font-size:12px;color:#64748b;">Loading bundles…</div>
					<div v-else class="row g-2">
						<div v-for="b in bundles" :key="b.id" class="col-12 col-md">
							<button type="button" @click="form.bundle_id = b.id"
								class="btn w-100 text-start rounded p-3 h-100"
								:style="form.bundle_id === b.id
									? { border:'2px solid #1d4ed8', background:'#eff6ff' }
									: { border:'1px solid #e2e8f0', background:'white' }">
								<div class="d-flex align-items-baseline justify-content-between gap-2 mb-1">
									<div class="fw-semibold text-dark" style="font-size:14px;">{{ b.name }}</div>
									<span v-if="recommendedBundleIds.includes(b.id)"
										class="rounded fw-bold text-uppercase px-2 py-0" style="font-size:9px;letter-spacing:0.05em;background:#dcfce7;color:#166534;">
										recommended
									</span>
								</div>
								<div class="mb-2" style="font-size:11px;color:#64748b;">{{ b.description }}</div>
								<div v-if="bundlePrice(b.id)" class="d-flex align-items-baseline gap-2">
									<span class="fw-bold font-monospace" style="font-size:14px;color:#1e293b;">
										{{ fmtCents(bundlePrice(b.id).firstYearCents) }}
									</span>
									<span style="font-size:10px;color:#64748b;">
										= {{ fmtCents(bundlePrice(b.id).setupCents) }} setup + {{ fmtCents(bundlePrice(b.id).annualCents) }}/yr
									</span>
								</div>
							</button>
						</div>
					</div>
				</div>

				<!-- Support tier -->
				<div class="rounded bg-white p-3 mb-3" style="border:1px solid #e2e8f0;">
					<h2 class="h6 fw-semibold text-dark mb-2">Support tier</h2>
					<div class="row g-2">
						<div v-for="t in supportTiers" :key="t.id" class="col-12 col-md-4">
							<button type="button" @click="form.support_tier_id = t.id"
								class="btn w-100 text-start rounded p-2"
								:style="form.support_tier_id === t.id
									? { border:'2px solid #1d4ed8', background:'#eff6ff' }
									: { border:'1px solid #e2e8f0', background:'white' }">
								<div class="fw-semibold text-dark" style="font-size:13px;">{{ t.name }}</div>
								<div style="font-size:11px;color:#64748b;">
									Annual surcharge: {{ t.surcharge ? `+${(t.surcharge * 100).toFixed(0)}%` : 'none' }}
								</div>
							</button>
						</div>
					</div>
				</div>

				<!-- Add-ons / line items -->
				<div v-if="lineItems.length" class="rounded bg-white p-3 mb-3" style="border:1px solid #e2e8f0;">
					<div class="d-flex align-items-baseline justify-content-between mb-2">
						<h2 class="h6 fw-semibold text-dark mb-0">Add-ons</h2>
						<span style="font-size:11px;color:#64748b;">Greyed rows are included in the selected bundle.</span>
					</div>
					<div v-for="(group, cat) in groupedLineItems" :key="cat" class="mb-3">
						<div class="text-uppercase fw-semibold mb-2" style="font-size:10px;letter-spacing:0.05em;color:#64748b;">{{ cat }}</div>
						<ul class="list-unstyled mb-0">
							<li v-for="item in group" :key="item.id" class="rounded mb-2 p-2"
								:style="{
									border: '1px solid #e2e8f0',
									background: includedInBundle(item) ? '#f8fafc' : 'white',
									opacity: includedInBundle(item) ? 0.7 : 1
								}">
								<div class="d-flex align-items-center gap-2">
									<input type="checkbox"
										:checked="isSelected(item.id)"
										:disabled="includedInBundle(item)"
										@change="toggleItem(item, $event.target.checked)"
										class="form-check-input m-0 flex-shrink-0" />
									<div class="flex-grow-1 min-w-0">
										<div class="fw-medium text-dark" style="font-size:13px;">
											{{ item.name }}
											<span v-if="includedInBundle(item)" class="ms-1 rounded fw-semibold text-uppercase px-2 py-0"
												style="font-size:9px;letter-spacing:0.05em;background:#dcfce7;color:#166534;">included</span>
										</div>
										<div style="font-size:11px;color:#64748b;">{{ item.description }}</div>
									</div>
									<!-- Per-unit qty input -->
									<template v-if="isSelected(item.id) && needsQty(item)">
										<input type="number" min="0" step="1"
											:value="qtyFor(item.id)"
											@input="updateQty(item.id, $event.target.value)"
											class="form-control form-control-sm font-monospace text-end flex-shrink-0"
											style="width:80px;" />
										<span style="font-size:11px;color:#64748b;width:48px;">{{ item.pricing.unitLabel || 'units' }}</span>
									</template>
									<!-- Tier dropdown -->
									<template v-if="isSelected(item.id) && needsTier(item)">
										<select class="form-select form-select-sm flex-shrink-0" style="max-width:200px;font-size:12px;"
											:value="tierFor(item.id)"
											@change="updateTier(item.id, $event.target.value)">
											<option v-for="(tier, idx) in item.pricing.tiers" :key="idx" :value="idx">
												{{ tier.label }}
											</option>
										</select>
									</template>
									<!-- Price summary for the item from breakdown -->
									<div v-if="lineItemPriceFor(item.id)" class="text-end font-monospace flex-shrink-0" style="font-size:12px;color:#0f172a;min-width:100px;">
										<div v-if="lineItemPriceFor(item.id).setupCents">+{{ fmtCents(lineItemPriceFor(item.id).setupCents) }} setup</div>
										<div v-if="lineItemPriceFor(item.id).annualCents">+{{ fmtCents(lineItemPriceFor(item.id).annualCents) }}/yr</div>
									</div>
								</div>
							</li>
						</ul>
					</div>
				</div>

				<!-- Notes -->
				<div class="rounded bg-white p-3 mb-3" style="border:1px solid #e2e8f0;">
					<label class="form-label mb-1" style="font-size:12px;font-weight:500;color:#475569;">Internal notes (not shown to customer)</label>
					<textarea v-model="form.notes" rows="3" class="form-control"
						placeholder="Discount justification, special terms, follow-up reminders…"></textarea>
				</div>

				<!-- Comparables: same state, ±50% of population. Closed by default
					so it doesn't steal attention from the live quote. -->
				<section v-if="form.state && form.population" class="rounded bg-white mb-3" style="border:1px solid #e2e8f0;">
					<header class="px-3 py-2 d-flex align-items-baseline justify-content-between"
						style="background:#f8fafc;border-bottom:1px solid #e2e8f0;cursor:pointer;user-select:none;"
						@click="comparablesOpen = !comparablesOpen">
						<div>
							<h3 class="h6 fw-semibold mb-0 text-dark">
								Comparable proposals in {{ form.state }}
								<span class="fw-normal" style="font-size:11px;color:#64748b;">
									pop {{ comparablesPopMin.toLocaleString() }}–{{ comparablesPopMax.toLocaleString() }}
								</span>
							</h3>
							<p class="mb-0 mt-1" style="font-size:11px;color:#64748b;">
								Same state, within ±50% of this muni's population ({{ Number(form.population).toLocaleString() }}). Sorted by signed date.
							</p>
						</div>
						<div class="d-flex align-items-baseline gap-3" style="font-size:11px;">
							<span>
								<span class="fw-medium" style="color:#047857;">{{ comparables.won.length }} won</span>
								· <span class="fw-medium" style="color:#be123c;">{{ comparables.lost.length }} lost</span>
								<span v-if="comparables.pending.length" class="ms-1" style="color:#92400e;">· {{ comparables.pending.length }} pending</span>
							</span>
							<span style="color:#94a3b8;">{{ comparablesOpen ? '▾' : '▸' }}</span>
						</div>
					</header>
					<div v-if="comparablesOpen">
						<div v-if="comparablesLoading" class="px-3 py-4 text-center" style="font-size:12px;color:#64748b;">Loading comparables…</div>
						<div v-else-if="comparablesError" class="px-3 py-4 text-center" style="font-size:12px;color:#9f1239;">{{ comparablesError }}</div>
						<div v-else-if="!comparables.won.length && !comparables.lost.length && !comparables.pending.length"
							class="px-3 py-4 text-center" style="font-size:12px;color:#64748b;">
							No past proposals in {{ form.state }} within this population band.
						</div>
						<div v-else class="row g-0">
							<div class="col-md-6 p-3" style="border-right:1px solid #e2e8f0;">
								<div class="text-uppercase fw-semibold mb-2" style="font-size:10px;letter-spacing:0.05em;color:#047857;">
									Won ({{ comparables.won.length }})
								</div>
								<ul v-if="comparables.won.length" class="list-unstyled mb-0 d-flex flex-column gap-1">
									<li v-for="r in comparables.won" :key="r.id" class="rounded p-2"
										style="border:1px solid #a7f3d0;background:rgba(236,253,245,0.4);font-size:12px;">
										<router-link :to="`/cwpe/proposals/${r.id}`" class="text-decoration-none" style="color:inherit;">
											<div class="d-flex align-items-baseline justify-content-between gap-2">
												<span class="fw-medium" style="color:#0f172a;">{{ r.municipality_name_raw }}, {{ r.state }}</span>
												<span class="flex-shrink-0" style="font-size:10px;color:#64748b;">{{ r.signed_date ? fmtCompDate(r.signed_date) : '—' }}</span>
											</div>
											<div class="d-flex align-items-baseline gap-2 flex-wrap mt-1" style="font-size:11px;color:#475569;">
												<span>Pop {{ r.population != null ? Number(r.population).toLocaleString() : '—' }}</span>
												<span class="font-monospace">{{ fmtCents(r.setup_cents) }} + {{ fmtCents(r.annual_cents) }}/yr</span>
												<span style="color:#64748b;">· {{ r.contract_years }}yr</span>
											</div>
										</router-link>
									</li>
								</ul>
								<div v-else class="fst-italic" style="font-size:11px;color:#94a3b8;">No won proposals in this band.</div>
							</div>
							<div class="col-md-6 p-3">
								<div class="text-uppercase fw-semibold mb-2" style="font-size:10px;letter-spacing:0.05em;color:#be123c;">
									Lost ({{ comparables.lost.length }})
								</div>
								<ul v-if="comparables.lost.length" class="list-unstyled mb-0 d-flex flex-column gap-1">
									<li v-for="r in comparables.lost" :key="r.id" class="rounded p-2"
										style="border:1px solid #fecdd3;background:rgba(255,241,242,0.4);font-size:12px;">
										<router-link :to="`/cwpe/proposals/${r.id}`" class="text-decoration-none" style="color:inherit;">
											<div class="d-flex align-items-baseline justify-content-between gap-2">
												<span class="fw-medium" style="color:#0f172a;">{{ r.municipality_name_raw }}, {{ r.state }}</span>
												<span class="flex-shrink-0" style="font-size:10px;color:#64748b;">{{ r.signed_date ? fmtCompDate(r.signed_date) : '—' }}</span>
											</div>
											<div class="d-flex align-items-baseline gap-2 flex-wrap mt-1" style="font-size:11px;color:#475569;">
												<span>Pop {{ r.population != null ? Number(r.population).toLocaleString() : '—' }}</span>
												<span class="font-monospace">{{ fmtCents(r.setup_cents) }} + {{ fmtCents(r.annual_cents) }}/yr</span>
												<span style="color:#64748b;">· {{ r.contract_years }}yr</span>
											</div>
										</router-link>
									</li>
								</ul>
								<div v-else class="fst-italic" style="font-size:11px;color:#94a3b8;">No lost proposals in this band.</div>
							</div>
						</div>
					</div>
				</section>

				<div v-if="quote" class="rounded bg-white p-3" style="border:1px solid #e2e8f0;font-size:12px;color:#64748b;">
					<span>ID: <code>{{ quote.id }}</code></span>
					<span class="ms-3">Created by: {{ quote.created_by || '—' }}</span>
					<span class="ms-3">Last updated: {{ formatDate(quote.updated_at) }}</span>
				</div>
			</template>
		</main>

		<!-- Delete confirmation modal -->
		<div v-if="showDeleteModal" class="modal fade show d-block" tabindex="-1" @click.self="cancelDelete">
			<div class="modal-dialog modal-dialog-centered">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">Delete this quote?</h5>
						<button type="button" class="btn-close" :disabled="deleting" @click="cancelDelete"></button>
					</div>
					<div class="modal-body">
						<p class="mb-1" style="font-size:14px;color:#334155;">
							<template v-if="quote">
								Quote for <strong>{{ quote.municipality_name_raw }}, {{ quote.state }}</strong> will be permanently deleted.
							</template>
							<template v-else>
								This quote will be permanently deleted.
							</template>
						</p>
						<p class="mb-0" style="font-size:12px;color:#64748b;">This cannot be undone.</p>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-sm btn-outline-secondary" :disabled="deleting" @click="cancelDelete">Cancel</button>
						<button type="button" class="btn btn-sm btn-danger" :disabled="deleting" @click="confirmDelete">
							<span v-if="deleting" class="spinner-border spinner-border-sm me-1"></span>
							Delete
						</button>
					</div>
				</div>
			</div>
		</div>
		<div v-if="showDeleteModal" class="modal-backdrop fade show"></div>
	</div>
</template>

<style>
@keyframes cwpePulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.3; } }

/* Keep the proposal preview pinned to the top of the scroll area while the
   rep scrolls through bundle / support / add-ons below. The wrapper is a
   direct child of <main>, so it sticks for the whole page scroll. Opaque
   white background + padding-bottom so scrolling content can't peek through
   the gap; replaces the wrapper's old mb-3. */
.cwpe-quote-preview-sticky {
	position: sticky;
	top: 0;
	z-index: 20;
	background: #fff;
	padding-top: 8px;
	padding-bottom: 12px;
	box-shadow: 0 6px 8px -6px rgba(15, 23, 42, 0.18);
}
</style>

<script>
import restApi from '../../api/index.js'

const ACTIVE_QUOTE_KEY = 'cwpe_active_quote'
const ACTIVE_MUNI_KEY = 'cwpe_active_muni'
const BUNDLE_KEY = 'cwpe_selected_bundle'

export default {
	name: 'CwpeQuoteDetail',
	data() {
		return {
			loading: false,
			loadError: null,
			quote: null,
			breakdown: null,
			bundles: [],
			supportTiers: [],
			lineItems: [],
			lineItemOverrides: {},
			relevanceBands: [],
			form: {
				municipality_id: null,
				municipality_name_raw: '',
				state: '',
				population: null,
				bundle_id: 'b2',
				support_tier_id: 'standard',
				horizon_years: 5,
				selections_json: [],
				notes: '',
			},
			// Pricing inputs (from lookup, not persisted in the quote model)
			pricingInputs: {
				populationTrendPct: null,
				countyTrendPct: null,
				stateTrendPct: null,
				snapRate: null,
			},
			simulateBundles: [],
			recommendedBundleIds: [],
			// Muni search
			muniQuery: '',
			muniSuggestions: [],
			muniShowDropdown: false,
			muniLoading: false,
			muniError: null,
			muniSearchTimer: null,
			// Auto-save
			saving: false,
			savedAt: null,
			hydrated: false,
			autosaveTimer: null,
			simulateTimer: null,
			comparablesOpen: false,
			comparablesLoading: false,
			comparablesError: null,
			comparablesRows: [],
			comparablesTimer: null,
			showDeleteModal: false,
			deleting: false,
		}
	},
	computed: {
		isNew() { return this.$route.params.id === 'new' },
		muniDisplayValue() {
			if (this.muniQuery !== '') return this.muniQuery
			if (this.form.municipality_name_raw) return `${this.form.municipality_name_raw}, ${this.form.state}`
			return ''
		},
		stateAdjustPctDisplay() {
			if (!this.form.state) return null
			// Read settings via simulate? Skip — show only if computed adjustment factors include a state-tier entry.
			const factor = (this.breakdown?.adjustmentFactors || []).find(f => f.key === 'state-tier')
			return factor ? factor.deltaPct * 100 : null
		},
		snapPctDisplay() {
			return this.pricingInputs.snapRate != null ? this.pricingInputs.snapRate * 100 : null
		},
		popTrendDisplay() {
			return this.pricingInputs.populationTrendPct != null ? this.pricingInputs.populationTrendPct * 100 : null
		},
		groupedLineItems() {
			const groups = {}
			for (const item of this.lineItems) {
				const cat = item.category || 'other'
				if (!groups[cat]) groups[cat] = []
				groups[cat].push(item)
			}
			return groups
		},
		comparablesPopMin() {
			return Math.max(0, Math.round((this.form.population || 0) * 0.5))
		},
		comparablesPopMax() {
			return Math.round((this.form.population || 0) * 1.5)
		},
		comparables() {
			const rows = this.comparablesRows.filter(r => r.id !== this.$route.params.id)
			return {
				won:     rows.filter(r => r.outcome === 'won'),
				lost:    rows.filter(r => r.outcome === 'lost'),
				pending: rows.filter(r => r.outcome === 'pending'),
			}
		},
	},
	created() {
		this.bootstrap()
	},
	beforeDestroy() {
		if (this.autosaveTimer) clearTimeout(this.autosaveTimer)
		if (this.simulateTimer) clearTimeout(this.simulateTimer)
		if (this.muniSearchTimer) clearTimeout(this.muniSearchTimer)
	},
	watch: {
		// Watch every editable field for auto-save
		'form.municipality_name_raw'() { this.scheduleAutosave() },
		'form.state'() { this.scheduleAutosave(); this.scheduleComparables() },
		'form.population'() { this.scheduleAutosave(); this.scheduleSimulate(); this.scheduleComparables() },
		'form.bundle_id'(v) {
			if (v) try { localStorage.setItem(BUNDLE_KEY, v) } catch (_) { /* ignore quota */ }
			this.scheduleAutosave()
			this.scheduleSimulate()
		},
		'form.support_tier_id'() { this.scheduleAutosave(); this.scheduleSimulate() },
		'form.horizon_years'() { this.scheduleAutosave() },
		'form.notes'() { this.scheduleAutosave() },
		'form.selections_json': { handler() { this.scheduleAutosave() }, deep: true },
	},
	methods: {
		async bootstrap() {
			this.loading = true
			try {
				const cfgRes = await restApi.get('/cwpe/pricing/config')
				const cfg = cfgRes.data
				this.bundles = cfg.bundles || []
				this.supportTiers = cfg.supportTiers || []
				this.lineItems = cfg.lineItems || []
				this.lineItemOverrides = cfg.settings?.line_item_overrides || {}
				this.relevanceBands = cfg.relevanceBands || []

				if (!this.isNew) {
					const r = await restApi.get(`/cwpe/quotes/${this.$route.params.id}`)
					this.quote = r.data.quote
					this.breakdown = r.data.breakdown
					this.form = {
						municipality_id: r.data.quote.municipality_id,
						municipality_name_raw: r.data.quote.municipality_name_raw,
						state: r.data.quote.state,
						population: r.data.quote.population,
						bundle_id: r.data.quote.bundle_id,
						support_tier_id: r.data.quote.support_tier_id || 'standard',
						horizon_years: r.data.quote.horizon_years || 5,
						selections_json: r.data.quote.selections_json || [],
						notes: r.data.quote.notes || '',
					}
					localStorage.setItem(ACTIVE_QUOTE_KEY, JSON.stringify({
						id: this.quote.id, title: `${this.quote.municipality_name_raw}, ${this.quote.state}`, updatedAt: this.quote.updated_at,
					}))
					// Sync the active-muni chip to this quote's municipality without
					// re-running /pricing/lookup — the quote already carries id,
					// name, state, and population.
					if (this.form.municipality_id) {
						this.persistActiveMuni({
							id: this.form.municipality_id,
							name: this.form.municipality_name_raw,
							full_name: this.form.municipality_name_raw,
							state: this.form.state,
							county: this.quote.county_name || null,
						})
					}
				} else {
					// Seed from cross-page localStorage. Active muni picks the
					// municipality, selected bundle pre-fills the bundle picker so
					// the rep doesn't lose their pick when bouncing between Lookup
					// / Pricing / Quote Builder.
					const muniRaw = localStorage.getItem(ACTIVE_MUNI_KEY)
					if (muniRaw) {
						try {
							const m = JSON.parse(muniRaw)
							// Inherit the municipality picked on the Lookup page —
							// including its id and population — without re-running
							// /pricing/lookup here (that 422'd on the missing id, and
							// the data is already cached from the Lookup page).
							this.form.municipality_id = m.id || null
							this.form.municipality_name_raw = m.name || m.full_name || ''
							this.form.state = m.state || ''
							if (m.population != null) this.form.population = m.population
						} catch (e) { /* ignore parse errors */ }
					}
					const savedBundle = localStorage.getItem(BUNDLE_KEY)
					if (savedBundle && this.bundles.find(b => b.id === savedBundle)) {
						this.form.bundle_id = savedBundle
					}
				}
				this.computeRecommended()
				this.scheduleSimulate()
				this.scheduleComparables()
			} catch (err) {
				this.loadError = err?.response?.data?.message || err?.message || 'Could not load quote'
			} finally {
				this.loading = false
				this.$nextTick(() => {
					this.hydrated = true
					// New quote seeded with a municipality: create it right away so
					// the Proposal preview (server-computed breakdown) shows
					// immediately, instead of only after the rep pokes a field
					// (e.g. toggles an add-on). Autosave is the existing create path;
					// it was just gated behind `hydrated`, so the seed never tripped it.
					if (this.isNew && this.form.municipality_name_raw && this.form.state) {
						this.scheduleAutosave()
					}
				})
			}
		},
		// ── Muni search ─────────────────────────────────────────────────
		// Mirror of CwpeLookup: nationwide /municipalities/search (no state
		// pre-filter — picked suggestion carries its own state) + Census /
		// SNAP / trend enrichment via /cwpe/pricing/lookup on select.
		onMuniInput(e) {
			this.muniQuery = e.target.value
			if (this.muniSearchTimer) clearTimeout(this.muniSearchTimer)
			const q = this.muniQuery.trim()
			if (q.length < 2) { this.muniSuggestions = []; this.muniShowDropdown = false; return }
			this.muniSearchTimer = setTimeout(() => {
				restApi.get('/municipalities/search', { params: { search: q, limit: 15 } }).then(({ data }) => {
					const list = Array.isArray(data) ? data : (data?.results || data?.rows || [])
					this.muniSuggestions = list.slice(0, 8)
					this.muniShowDropdown = this.muniSuggestions.length > 0
				}).catch(() => { this.muniSuggestions = []; this.muniShowDropdown = false })
			}, 200)
		},
		onMuniFocus() { if (this.muniSuggestions.length) this.muniShowDropdown = true },
		onMuniBlur() { setTimeout(() => { this.muniShowDropdown = false }, 200) },
		selectMuni(s) {
			this.muniShowDropdown = false
			this.muniQuery = `${s.name}, ${s.state}`
			this.form.municipality_id = s.id
			this.form.municipality_name_raw = s.name
			this.form.state = s.state
			// Push to active muni immediately so the parent header chip
			// reflects the pick before the (slower) Census lookup completes.
			this.persistActiveMuni({
				id: s.id,
				name: s.full_name || s.name,
				full_name: s.full_name || s.name,
				state: s.state,
				county: s.county || null,
			})
			// Prefer mc2-stored coords (lat/lng on municipalities row) — saves
			// a Nominatim hit and avoids cases like "Township Of Wheeling"
			// that don't resolve by name on OSM. Fall back to the autocomplete
			// `coordinates` field if a future Census-side search returns one.
			const lat = parseFloat(s.lat)
			const lon = parseFloat(s.lng != null ? s.lng : s.lon)
			let coords = null
			if (Number.isFinite(lat) && Number.isFinite(lon)) coords = { lat, lon }
			else if (s.coordinates && s.coordinates.lat && s.coordinates.lon) coords = s.coordinates
			this.refreshLookup(s.name, s.state, coords)
		},
		persistActiveMuni(muniObj) {
			localStorage.setItem(ACTIVE_MUNI_KEY, JSON.stringify(muniObj))
			window.dispatchEvent(new CustomEvent('cwpe:active-muni-changed'))
		},
		async refreshLookup(name, state, coords) {
			this.muniLoading = true
			this.muniError = null
			try {
				const r = await restApi.post('/cwpe/pricing/lookup', {
					municipality_id: this.form.municipality_id,
					name,
					state: state.toUpperCase(),
					coords: coords || undefined,
				})
				const md = r.data.municipality_data || {}
				this.form.population = md.population ?? this.form.population
				this.pricingInputs.populationTrendPct = md.population_trend_pct ?? null
				this.pricingInputs.countyTrendPct = md.county_population_trend_pct ?? null
				this.pricingInputs.stateTrendPct = md.state_population_trend_pct ?? null
				this.pricingInputs.snapRate = md.snap_rate ?? null
				// Re-persist with the resolved county so the header chip + the
				// Lookup page's seed-from-active-muni flow both have it.
				this.persistActiveMuni({
					id: this.form.municipality_id,
					name: this.form.municipality_name_raw,
					full_name: this.form.municipality_name_raw,
					state: this.form.state,
					county: md.county_name || null,
				})
				this.computeRecommended()
				this.scheduleSimulate()
			} catch (e) {
				this.muniError = e?.response?.data?.message || e?.message || 'Lookup failed'
			} finally {
				this.muniLoading = false
			}
		},
		// ── Bundle picker prices ────────────────────────────────────────
		scheduleSimulate() {
			if (this.simulateTimer) clearTimeout(this.simulateTimer)
			this.simulateTimer = setTimeout(this.runSimulate, 250)
		},
		runSimulate() {
			if (this.form.population == null || !this.form.state) {
				this.simulateBundles = []
				return
			}
			restApi.post('/cwpe/pricing/simulate', {
				population: this.form.population,
				population_trend_pct: this.pricingInputs.populationTrendPct,
				county_trend_pct: this.pricingInputs.countyTrendPct,
				state_trend_pct: this.pricingInputs.stateTrendPct,
				snap_rate: this.pricingInputs.snapRate,
				state: this.form.state.toUpperCase(),
				support_tier_id: this.form.support_tier_id,
				contract_years: this.form.horizon_years,
			}).then(({ data }) => {
				this.simulateBundles = data.bundles || []
				if (data.recommendedBundleIds) this.recommendedBundleIds = data.recommendedBundleIds

				// New quotes: if the bundle prefilled from cross-page storage
				// is out of band for this muni's population (e.g. b5 from a
				// previous 100k lookup → now 300-pop muni), correct to first
				// recommended. Existing quotes never get auto-corrected — the
				// rep's saved choice is authoritative.
				if (this.isNew && this.recommendedBundleIds.length
					&& this.form.bundle_id
					&& !this.recommendedBundleIds.includes(this.form.bundle_id)) {
					this.form.bundle_id = this.recommendedBundleIds[0]
				}
			}).catch(() => { /* simulate failures shouldn't block UI */ })
		},
		// ── Comparables ────────────────────────────────────────────────
		scheduleComparables() {
			if (this.comparablesTimer) clearTimeout(this.comparablesTimer)
			this.comparablesTimer = setTimeout(this.loadComparables, 350)
		},
		loadComparables() {
			if (!this.form.state || !this.form.population) {
				this.comparablesRows = []
				return
			}
			this.comparablesLoading = true
			this.comparablesError = null
			restApi.get('/cwpe/proposals', {
				params: {
					state: this.form.state.toUpperCase(),
					population_min: this.comparablesPopMin,
					population_max: this.comparablesPopMax,
					sort: 'signedDate',
					dir: 'desc',
					per_page: 50,
				},
			}).then(({ data }) => {
				this.comparablesRows = data.data || []
			}).catch(err => {
				this.comparablesError = err?.response?.data?.message || 'Lookup failed'
				this.comparablesRows = []
			}).finally(() => { this.comparablesLoading = false })
		},
		fmtCompDate(val) {
			if (!val) return '—'
			return new Date(val).toLocaleDateString('en-US', { year: '2-digit', month: 'short', day: 'numeric' })
		},
		bundlePrice(id) { return this.simulateBundles.find(b => b.bundleId === id) || null },
		bundleName(id) { const b = this.bundles.find(x => x.id === id); return b ? b.name : id },
		supportTierName(id) { const t = this.supportTiers.find(x => x.id === id); return t ? t.name : id },
		computeRecommended() {
			if (this.recommendedBundleIds.length) return
			if (this.form.population == null || !this.relevanceBands.length) return
			const sorted = [...this.relevanceBands].sort((a, b) => b.minPop - a.minPop)
			const band = sorted.find(b => this.form.population >= b.minPop)
			if (band) this.recommendedBundleIds = band.recommendedBundleIds || []
		},
		// ── Add-ons ─────────────────────────────────────────────────────
		isSelected(id) { return this.form.selections_json.some(s => s.id === id) },
		includedInBundle(item) {
			return Array.isArray(item.includedInBundles) && item.includedInBundles.includes(this.form.bundle_id)
		},
		needsQty(item) {
			return ['per-unit-annual', 'per-unit-setup'].includes(item.pricing?.kind)
		},
		needsTier(item) {
			return ['tier-annual', 'tier-setup'].includes(item.pricing?.kind)
		},
		qtyFor(id) {
			const sel = this.form.selections_json.find(s => s.id === id)
			return sel?.qty ?? 1
		},
		tierFor(id) {
			const sel = this.form.selections_json.find(s => s.id === id)
			return sel?.tierIndex ?? 0
		},
		toggleItem(item, on) {
			if (on) {
				const sel = { id: item.id }
				if (this.needsQty(item)) sel.qty = item.pricing.includedQty || 1
				if (this.needsTier(item)) sel.tierIndex = 0
				this.form.selections_json = [...this.form.selections_json, sel]
			} else {
				this.form.selections_json = this.form.selections_json.filter(s => s.id !== item.id)
			}
		},
		updateQty(id, raw) {
			const qty = Math.max(0, Number(raw) || 0)
			this.form.selections_json = this.form.selections_json.map(s => s.id === id ? { ...s, qty } : s)
		},
		updateTier(id, raw) {
			const idx = Number(raw) || 0
			this.form.selections_json = this.form.selections_json.map(s => s.id === id ? { ...s, tierIndex: idx } : s)
		},
		lineItemPriceFor(id) {
			return (this.breakdown?.lineItems || []).find(r => r.itemId === id) || null
		},
		// ── Auto-save ───────────────────────────────────────────────────
		scheduleAutosave() {
			if (!this.hydrated) return
			if (!this.form.municipality_name_raw || !this.form.state) return
			if (this.autosaveTimer) clearTimeout(this.autosaveTimer)
			this.autosaveTimer = setTimeout(this.runSave, 600)
		},
		runSave() {
			if (this.saving) return
			this.saving = true
			const body = {
				municipality_id: this.form.municipality_id,
				municipality_name_raw: this.form.municipality_name_raw,
				state: (this.form.state || '').toUpperCase(),
				population: this.form.population,
				bundle_id: this.form.bundle_id,
				support_tier_id: this.form.support_tier_id,
				horizon_years: this.form.horizon_years,
				selections_json: this.form.selections_json,
				notes: this.form.notes || null,
			}
			const op = this.isNew
				? restApi.post('/cwpe/quotes', body)
				: restApi.put(`/cwpe/quotes/${this.$route.params.id}`, body)
			op.then(async ({ data }) => {
				const persistedId = data.id || this.$route.params.id
				if (this.isNew) {
					this.$router.replace(`/cwpe/quotes/${persistedId}`)
				}
				this.savedAt = new Date()
				localStorage.setItem(ACTIVE_QUOTE_KEY, JSON.stringify({
					id: persistedId,
					title: `${body.municipality_name_raw}, ${body.state}`,
					updatedAt: this.savedAt.toISOString(),
				}))
				// Refetch breakdown so the preview reflects server-computed pricing
				try {
					const r = await restApi.get(`/cwpe/quotes/${persistedId}`)
					this.quote = r.data.quote
					this.breakdown = r.data.breakdown
				} catch (e) { /* don't fail save UX on refetch error */ }
			}).catch(err => {
				console.error('Quote autosave failed', err)
			}).finally(() => { this.saving = false })
		},
		onDelete() {
			this.showDeleteModal = true
		},
		cancelDelete() {
			if (this.deleting) return
			this.showDeleteModal = false
		},
		confirmDelete() {
			if (this.deleting) return
			this.deleting = true
			restApi.delete(`/cwpe/quotes/${this.$route.params.id}`).then(() => {
				localStorage.removeItem(ACTIVE_QUOTE_KEY)
				this.$router.push('/cwpe/quotes')
			}).catch(err => {
				this.deleting = false
				this.showDeleteModal = false
				this.$swal.fire({ icon: 'error', title: 'Delete failed', text: err?.response?.data?.error || '' })
			})
		},
		fmtCents(c) {
			if (c == null) return '—'
			return '$' + (c / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
		},
		formatDate(val) {
			if (!val) return '—'
			return new Date(val).toLocaleString()
		},
	},
}
</script>
